import PropTypes from "prop-types"
import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { enqueueSnackbar } from "notistack";
import { useParams, Link as RouterLink } from "react-router-dom";
import { Box, Stack, Chip, Link } from "@mui/material";
import Iconify from "../../../../../../reusable-components/iconify";
import { useScopeCheck } from "../../../../../../reusable-components/scopes";
import FacilityContacts from "../../FacilityContacts";
import ManagerChips from "../../../../../../reusable-components/datagrid/ManagerChips";
import { getTicketData } from "../../../../../../redux/slices/tickets";

TicketInformation.propTypes = {
  facility: PropTypes.object
}

export default function TicketInformation({facility}) {
  const { id = '' } = useParams();
  const { mainTicket, ticketManagers = []} = useSelector((state) => state.tickets.data.ticketInfoById[id]) || {};
  
  const updateManagerParamsRows = (updatedRow) => {
    setManagerParams(prev => ({
      ...prev,
      row: {
        ...prev.row,
        ...updatedRow[0],
      }
    }));
  }
  
  const [managerParams, setManagerParams] = useState(id ? {
    row: {
      facilityManagerAssignments: ticketManagers,
        facilityId: facility?.id,
        id,
      },
      api: { updateRows: updateManagerParamsRows }
    } : null);
  const hasFacilityAccess = useScopeCheck(['Facility-Manager'], true);
  const dispatch = useDispatch();


  useEffect(() => {
    if(!id) return;
    setManagerParams({
      row: {
        facilityManagerAssignments: ticketManagers,
        facilityId: facility?.id,
        id,
      },
      api: { updateRows: updateManagerParamsRows }
    })
  }, [ticketManagers, facility, id]);

  function parseAddress(address) {
    const match = address?.match(/^(.+?),\s*([A-Za-z\s]+)\s+([A-Z]{2})\s+(\d{5})$/);
    if (!match) return { street: address, city: '', state: '', zip: '' };
    return {
      street: match[1],
      city: match[2].trim(),
      state: match[3],
      zip: match[4],
    };
  }

  const { street, city, state, zip } = parseAddress(facility?.address);

  const iconContainerStyles = {
    mr: 1,
    width: 14,
    height: 14,
    display: 'flex',
    flexShrink: 0,
  }

  return <Stack spacing={0.5} sx={{ ml: 1, mt: 1, fontSize: '14px' }}>
    {(street || city || state || zip) && <Stack direction="row" alignItems="start">
      <Box sx={iconContainerStyles}>
        <Iconify icon="bi:house" color="secondary"/>
      </Box>
       <Link
        target="_blank"
        rel="noopener"
        display="inline"
        to={`https://www.google.com/maps/place/${facility?.address}`}
        component={RouterLink}
        sx={{ textDecoration: 'underline', '&:hover': { color: 'primary.light' } }}
        >
          {street}
          {street && <br />}
          {city}{city && state && ', '} {state} {zip}
      </Link>
    </Stack>}
    {facility?.phoneNumber && <Stack direction="row" alignItems="start">
      <Box sx={iconContainerStyles}>
        <Iconify icon="bi:telephone" color="secondary"/>
      </Box>
      <span>{facility?.phoneNumber}</span>
    </Stack>}
    <Stack direction="row" alignItems="start">
      <Box sx={iconContainerStyles}>
        <Iconify icon="lucide:bed" color="secondary"/>
      </Box>
      <span>{facility?.beds} Beds</span>
    </Stack>
    <Stack direction="row" alignItems="start">
      <Box sx={iconContainerStyles}>
        <Iconify icon="bi:person" color="secondary"/>
      </Box>
      <span>Census {facility?.censusNumber}</span>
    </Stack>
    {((ticketManagers?.length > 0 || hasFacilityAccess) && !!managerParams?.row && !!managerParams?.api) && <Stack direction="row" alignItems="start" sx={{ flexWrap: 'wrap' }}>
      <Box sx={iconContainerStyles}>
        <Iconify icon="hugeicons:manager" color="secondary"/>
      </Box>
      <Box sx={{mr: 1}}>Managers</Box>
      <Box>
        {
          !mainTicket?.complete ?
            <ManagerChips 
              params={managerParams}
              type="Tickets"
              hasFacilityAccess={hasFacilityAccess}
              refetch={() => dispatch(getTicketData(id))}
              onDeleteSuccess={() => enqueueSnackbar('Manager deleted successfully', { variant: 'success' })}
            /> : ticketManagers.map(manager => <Chip
              label={manager.manager}
              key={manager.facilityManagerId}
              size="small"
              sx={{ ml: 0.5 }}
              color="primary"
            />)
        }
      </Box>
    </Stack>}
    <FacilityContacts facilityContacts={facility?.facilityContacts || []} />
  </Stack>
}